import { api, UserUpdate } from '@/shared/lib/api'
import { updateUser, useUser } from '@/shared/store/auth.store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/Button'
import { Card } from '@workspace/ui/components/Card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@workspace/ui/components/Form'
import { formatRegionAreaText } from '@/shared/components/user/RegionAreaDisplay'
import { toast } from '@workspace/ui/components/Sonner'
import { Input, PasswordInput, PhoneInput } from '@workspace/ui/components/Textfield'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import type { TFunction } from 'i18next'
import { passwordMatchRefine, requiredPasswordValidation } from '@/shared/validations/password-validation'
import { handleApiError } from '@/shared/lib/errorHandler'

export const Route = createFileRoute('/profile')({
    component: ProfilePage,
})

// Define schema as a function to inject translations
const createProfileSchema = (t: (key: string) => string) =>
    z.object({
        fullName: z.string().trim().min(1, t('profile.validation.fullNameRequired')),
        position: z.string().optional(),
        email: z.string().email(),
        phone: z
            .string()
            .regex(/^(?:$|\+84(3|5|7|8|9)\d{8})$/, {
                message: t('profile.validation.phoneInvalid'),
            })
            .optional(),
        role: z.string(),
        department: z.string(),
        region: z.string(),
        area: z.string(),
    })

type ProfileFormData = z.infer<ReturnType<typeof createProfileSchema>>

const createChangePasswordSchema = (t: TFunction<'translation', undefined>) =>
    z
        .object({
            currentPassword: z.string().min(1, t('profile.password.validation.currentPasswordRequired')),
            newPassword: requiredPasswordValidation(t),
            confirmPassword: z.string().optional(),
        })
        .refine(data => data.newPassword === data.confirmPassword, passwordMatchRefine(t))

type ChangePasswordFormData = z.infer<ReturnType<typeof createChangePasswordSchema>>

function ProfilePage() {
    const { t } = useTranslation()
    const user = useUser()
    const { data: profileUser } = useQuery({
        queryKey: ['user', 'profile', user?.id],
        queryFn: () => api.auth.getMe(),
        enabled: !!user?.id,
        staleTime: 0,
        refetchOnMount: 'always',
    })

    const displayUser = profileUser ?? user
    const { regionName, areaDisplay } = formatRegionAreaText(displayUser?.region, displayUser?.areas)

    useEffect(() => {
        if (profileUser) {
            updateUser(profileUser)
        }
    }, [profileUser])

    const [avatarPreview, setAvatarPreview] = useState<string | null>(displayUser?.avatar || null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Memoize schema to avoid recreation on every render
    const profileSchema = createProfileSchema(t)

    // Mock default data if user is missing some fields for demo
    const defaultValues: ProfileFormData = {
        fullName: displayUser?.fullName || '',
        position: displayUser?.position || '',
        email: displayUser?.email || '',
        phone: displayUser?.phone || '',
        role: displayUser?.role?.displayName || displayUser?.role?.name || '',
        department: displayUser?.department?.name || '',
        region: regionName === '-' ? '' : regionName,
        area: areaDisplay === '-' ? '' : areaDisplay,
    }

    const form = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema),
        defaultValues,
        mode: 'onChange',
    })

    const changePasswordSchema = createChangePasswordSchema(t)
    const canChangePassword = !displayUser?.loginProvider || displayUser.loginProvider === 'local'
    const passwordForm = useForm<ChangePasswordFormData>({
        resolver: zodResolver(changePasswordSchema),
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        mode: 'onChange',
    })

    // Reset form when user loads
    useEffect(() => {
        if (displayUser) {
            const { regionName: region, areaDisplay: area } = formatRegionAreaText(
                displayUser.region,
                displayUser.areas,
            )

            form.reset({
                fullName: displayUser.fullName || '',
                position: displayUser.position || '',
                email: displayUser.email || '',
                phone: displayUser.phone || '',
                role: displayUser.role?.displayName || displayUser.role?.name || '',
                department: displayUser.department?.name || '',
                region: region === '-' ? '' : region,
                area: area === '-' ? '' : area,
            })
            setAvatarPreview(displayUser.avatar || null)
            setSelectedFile(null)
        }
    }, [displayUser, form])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            if (file.size > 50 * 1024 * 1024) {
                toast.error({ title: t('profile.toast.fileTooLarge', { max: '50MB' }) })
                return
            }
            setSelectedFile(file)
            // Create preview
            const reader = new FileReader()
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string)
            }
            reader.readAsDataURL(file)
            toast.success({ title: t('profile.toast.imageSelected') })
        }
    }

    const onSubmit = async (data: ProfileFormData) => {
        if (!displayUser) return

        try {
            if (selectedFile) {
                await api.user.updateAvatar(selectedFile)
            }

            const payload: UserUpdate = {
                fullName: data.fullName,
                email: displayUser.email,
                phone: data.phone || '',
                position: data.position || '',
                status: displayUser.status,
                roleId: displayUser.role.id,
                departmentId: displayUser.department?.id || '',
                areaIds: displayUser.areas?.map(a => a.id) || [],
                regionId: displayUser.region?.id,
            }

            const updatedUser = await api.user.updateMe(payload)

            // Update store
            updateUser(updatedUser)
            toast.success({ title: t('profile.toast.saveSuccess') })

            // Clear selected file after successful update
            setSelectedFile(null)
        } catch (error) {
            console.error('Failed to update profile:', error)
            handleApiError(error)
        }
    }

    const onChangePassword = async (data: ChangePasswordFormData) => {
        if (!displayUser) return

        try {
            await api.user.changePassword({
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
            })

            passwordForm.reset({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            })
            toast.success({ title: t('profile.password.toast.success') })
        } catch (error) {
            console.error('Failed to change password:', error)
            handleApiError(error)
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">{t('profile.title')}</h1>
                <Button
                    onClick={form.handleSubmit(onSubmit)}
                    className="bg-primary hover:bg-primary/80 font-medium min-w-[100px] rounded-lg"
                >
                    {t('profile.save')}
                </Button>
            </div>

            <Card className="p-4 shadow-sm border gap-4 rounded-xl bg-card text-card-foreground">
                {/* Avatar Section */}
                <div>
                    <label htmlFor="avatar" className="block text-sm font-medium mb-2">
                        {t('profile.avatar')}
                    </label>
                    <div className="flex items-end gap-4">
                        <div className="relative w-32 h-32 rounded-lg bg-primary-50 flex items-center justify-center overflow-hidden border">
                            {avatarPreview ? (
                                <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-2xl font-bold">
                                    {displayUser?.fullName ? displayUser.fullName.charAt(0).toUpperCase() : 'U'}
                                </span>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/png, image/jpeg"
                                onChange={handleFileChange}
                            />

                            {avatarPreview ? (
                                <Button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-primary hover:bg-primary/80 border-none rounded-lg"
                                >
                                    {t('profile.changeAvatar')}
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="bg-primary hover:bg-primary/80 border-none rounded-lg"
                                >
                                    {t('profile.uploadAvatar')}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Full Name */}
                            <FormField
                                control={form.control}
                                name="fullName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex gap-1">
                                            {t('profile.fullName')} <span className="text-red-500">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t('profile.placeholder.fullName')}
                                                {...field}
                                                className="h-10 rounded-lg"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Job Title -> Position */}
                            <FormField
                                control={form.control}
                                name="position"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('profile.position')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t('profile.placeholder.position')}
                                                {...field}
                                                className="h-10 rounded-lg"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Email */}
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('profile.email')}</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled className="bg-muted h-10 rounded-lg" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Phone Number -> Phone */}
                            <FormField
                                control={form.control}
                                name="phone"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('profile.phone')}</FormLabel>
                                        <FormControl>
                                            <PhoneInput
                                                {...field}
                                                placeholder={t('profile.placeholder.phone')}
                                                className="h-10"
                                                maxDigits={9}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Role */}
                            <FormField
                                control={form.control}
                                name="role"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('profile.role')}</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled className="bg-muted h-10 rounded-lg" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Unit -> Department */}
                            <FormField
                                control={form.control}
                                name="department"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('profile.department')}</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled className="bg-muted h-10" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Region */}
                            <FormField
                                control={form.control}
                                name="region"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('users.forms.editUser.labels.region')}</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled className="bg-muted h-10 rounded-lg" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Area */}
                            <FormField
                                control={form.control}
                                name="area"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('users.forms.editUser.labels.area')}</FormLabel>
                                        <FormControl>
                                            <Input {...field} disabled className="bg-muted h-10 rounded-lg" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </form>
                </Form>
            </Card>

            {canChangePassword ? (
                <Card className="p-4 shadow-sm border gap-4 rounded-xl bg-card text-card-foreground">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">{t('users.forms.editUser.labels.changePassword')}</h2>
                        <Button
                            onClick={passwordForm.handleSubmit(onChangePassword)}
                            className="bg-primary hover:bg-primary/80 font-medium min-w-[100px] rounded-lg"
                        >
                            {t('profile.password.save')}
                        </Button>
                    </div>

                    <Form {...passwordForm}>
                        <form onSubmit={passwordForm.handleSubmit(onChangePassword)}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={passwordForm.control}
                                    name="currentPassword"
                                    render={({ field }) => (
                                        <FormItem className="md:col-span-2">
                                            <FormLabel className="flex gap-1">
                                                {t('profile.password.currentPassword')}{' '}
                                                <span className="text-red-500">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    placeholder={t('profile.password.placeholder.currentPassword')}
                                                    {...field}
                                                    className="h-10 rounded-lg"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={passwordForm.control}
                                    name="newPassword"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="flex gap-1">
                                                {t('users.forms.editUser.labels.password')}{' '}
                                                <span className="text-red-500">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    placeholder={t('users.forms.editUser.placeholders.password')}
                                                    {...field}
                                                    className="h-10 rounded-lg"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={passwordForm.control}
                                    name="confirmPassword"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="flex gap-1">
                                                {t('users.forms.editUser.labels.confirmPassword')}{' '}
                                                <span className="text-red-500">*</span>
                                            </FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    placeholder={t('users.forms.editUser.placeholders.confirmPassword')}
                                                    {...field}
                                                    className="h-10 rounded-lg"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </form>
                    </Form>
                </Card>
            ) : (
                <Card className="p-4 shadow-sm border gap-2 rounded-xl bg-card text-card-foreground">
                    <h2 className="text-lg font-semibold">{t('users.forms.editUser.labels.changePassword')}</h2>
                    <p className="text-sm text-muted-foreground">{t('profile.password.notAllowed')}</p>
                </Card>
            )}
        </div>
    )
}
